/* eslint-disable react/no-unknown-property */
import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, Text } from "@react-three/drei";
import * as THREE from "three";

const BASE_COLORS = { A: "#22d3ee", T: "#f59e0b", G: "#a855f7", C: "#22c55e" };

const DNA = ({ turns = 4, radius = 0.6, height = 6, steps = 60 }) => {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    if (groupRef.current) groupRef.current.rotation.y = clock.getElapsedTime() * 0.35;
  });

  const nodes = useMemo(() => {
    const arr = [];
    const bases = ["A", "T", "G", "C"];
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const y = height / 2 - t * height;
      const angle = t * turns * Math.PI * 2;
      const x1 = radius * Math.cos(angle);
      const z1 = radius * Math.sin(angle);
      const x2 = radius * Math.cos(angle + Math.PI);
      const z2 = radius * Math.sin(angle + Math.PI);
      const b = bases[i % 4];
      const pair = b === "A" ? "T" : b === "T" ? "A" : b === "G" ? "C" : "G";
      arr.push({ x1, y, z1, x2, z2, b1: b, b2: pair });
    }
    return arr;
  }, [turns, radius, height, steps]);

  return (
    <group ref={groupRef}>
      {nodes.map((n, i) => (
        <group key={i}>
          {/* Left backbone */}
          <mesh position={[n.x1, n.y, n.z1]}>
            <sphereGeometry args={[0.09, 12, 12]} />
            <meshStandardMaterial color="#0ea5e9" metalness={0.4} roughness={0.3} emissive="#0369a1" emissiveIntensity={0.3} />
          </mesh>
          {/* Right backbone */}
          <mesh position={[n.x2, n.y, n.z2]}>
            <sphereGeometry args={[0.09, 12, 12]} />
            <meshStandardMaterial color="#f43f5e" metalness={0.4} roughness={0.3} emissive="#9f1239" emissiveIntensity={0.3} />
          </mesh>
          {/* Base pair rung */}
          {i % 2 === 0 && (
            <RungBar x1={n.x1} y={n.y} z1={n.z1} x2={n.x2} z2={n.z2} b1={n.b1} b2={n.b2} />
          )}
        </group>
      ))}
    </group>
  );
};

const RungBar = ({ x1, y, z1, x2, z2, b1, b2 }) => {
  const mid = [(x1 + x2) / 2, y, (z1 + z2) / 2];
  const len = Math.hypot(x2 - x1, z2 - z1);
  const angle = Math.atan2(z2 - z1, x2 - x1);
  return (
    <group position={mid} rotation={[0, -angle, 0]}>
      <mesh position={[-len / 4, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.05, 0.05, len / 2, 8]} />
        <meshStandardMaterial color={BASE_COLORS[b1]} emissive={BASE_COLORS[b1]} emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[len / 4, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.05, 0.05, len / 2, 8]} />
        <meshStandardMaterial color={BASE_COLORS[b2]} emissive={BASE_COLORS[b2]} emissiveIntensity={0.35} />
      </mesh>
    </group>
  );
};

const DNAHelix3D = () => {
  return (
    <Canvas camera={{ position: [4, 1, 5], fov: 45 }} dpr={[1, 2]}>
      <color attach="background" args={["#020617"]} />
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 5]} intensity={1.2} color="#22d3ee" />
      <pointLight position={[-5, -5, -5]} intensity={0.8} color="#a855f7" />

      <DNA />

      <Text position={[0, 4, 0]} fontSize={0.4} color="#22d3ee" anchorX="center">
        DNA · Double Helix
      </Text>
      <Text position={[0, -4, 0]} fontSize={0.18} color="#94a3b8" anchorX="center">
        A – T (cyan · amber) · G – C (purple · green)
      </Text>

      <Environment preset="night" />
      <OrbitControls enablePan={false} minDistance={4} maxDistance={12} autoRotate={false} />
    </Canvas>
  );
};

export default DNAHelix3D;
